import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import {
  PageActionStrip,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  HealthIndicator,
  StagePipeline,
  GateDecisionPanel,
  EvidenceList,
  FinancialConflictCard,
  LineageConflictCard,
  AiCitationStrip,
  AlertCard,
  PageViewerControls,
  KpiHeroBand,
  deriveKpiTone,
  InlineScoreCell,
  WorkflowPipeline,
  buildPipelineSteps,
  FinancialWaterfall,
  toast,
  activityRail,
} from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { AttachEvidenceDrawer } from './AttachEvidenceDrawer.jsx';
import { ProvenanceBadge } from '../../components/ProvenanceBadge.jsx';
import './gateReview.css';
import './gateReview.redesign.css';

function gateStatus(g, idx, activeIdx) {
  const s = String(g.status || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (s === 'approved' || s === 'complete') return 'done';
  if (s === 'rejected' || s === 'blocked' || s === 'revisionrequested') return 'blocked';
  if (idx === activeIdx) return 'active';
  return idx < activeIdx ? 'done' : 'upcoming';
}

function finiteFinancialNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

// Canonical lifecycle order for the compact gate-progression ribbon. Derived
// from the seeded `gate.stage` enum (intake → sustainment).
const LIFECYCLE_ORDER = ['intake', 'businessCase', 'approval', 'execution', 'sustainment'];
const LIFECYCLE_LABELS = {
  intake: 'Intake',
  businessCase: 'Business Case',
  approval: 'Approval',
  execution: 'Execution',
  sustainment: 'Sustainment',
};

// Per-criterion completeness score (0–100) used by the InlineScoreCell. Maps
// the real criterion state to a confidence-of-completion read so reviewers can
// scan progress without expanding every row. No fabricated value — derived
// directly from the criterion's recorded state.
function criterionScore(state) {
  switch (state) {
    case 'complete':
    case 'verified':
      return 100;
    case 'inReview':
    case 'in_review':
      return 60;
    case 'exception':
      return 45;
    case 'atRisk':
    case 'at_risk':
      return 30;
    case 'blocked':
      return 10;
    default:
      return 0; // pending / missing
  }
}

function criterionScoreTone(state) {
  switch (state) {
    case 'complete':
    case 'verified':
      return 'sage';
    case 'inReview':
    case 'in_review':
      return 'blue';
    case 'exception':
    case 'atRisk':
    case 'at_risk':
      return 'amber';
    case 'blocked':
      return 'brick';
    default:
      return 'slate';
  }
}

// Days until a date string (rounded). Returns null when unparseable.
function daysUntil(dateString) {
  if (!dateString) return null;
  const target = new Date(dateString);
  if (Number.isNaN(target.getTime())) return null;
  const diffMs = target.getTime() - Date.now();
  return Math.round(diffMs / 86_400_000);
}

function trustFromRecord(record) {
  return {
    freshness: record?.freshness || record?.dataTrust?.freshness || 'unknown',
    confidence: record?.confidence ?? record?.dataTrust?.confidence ?? 'unknown',
  };
}

function makeFinancialConflictView({
  source,
  target,
  sourceLabel = 'Approved plan',
  targetLabel = 'Forecast at completion',
  freshness = 'unknown',
  confidence = 'unknown',
}) {
  const sourceNumber = finiteFinancialNumber(source);
  const targetNumber = finiteFinancialNumber(target);
  if (sourceNumber == null || targetNumber == null) return null;
  return {
    source: sourceNumber,
    target: targetNumber,
    variance: Number((targetNumber - sourceNumber).toFixed(2)),
    sourceLabel,
    targetLabel,
    freshness,
    confidence,
  };
}

export function ProjectGateReviewPage() {
  const { projectId, gateId } = useParams();
  const navigate = useNavigate();
  const project = useApi(`/projects/${projectId}`);
  const gate = useApi(`/projects/${projectId}/gates/${gateId}`);
  const gates = useApi(`/projects/${projectId}/gates`);
  const artifacts = useApi(`/artifacts`, { query: { scopeId: projectId, scopeType: 'project' } });
  const financials = useApi(`/projects/${projectId}/financials`);
  const { user, roles = [] } = useCurrentUser();
  const canSeeLineage = rbac.canSeeRoute('/admin/lineage', roles);
  const lineage = useApi(`/lineage`, {
    query: { targetEntityType: 'project', targetEntityId: projectId, conflictOnly: '1' },
    skip: !canSeeLineage,
  });
  const aiDraft = useMutation();
  const decision = useMutation();
  const attach = useMutation();
  const [aiBrief, setAiBrief] = useState(null);
  // AC-P3M-5: attaching the AI brief as decision rationale is an explicit
  // opt-in, unchecked by default — the brief is never silently included.
  const [useAiRationale, setUseAiRationale] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const p = project.data?.data;
  const g = gate.data?.data;
  const criteria = (g?.criteria || g?.checklist || []).map((c, i) => ({
    id: c.id || `crit-${i}`,
    title: c.name || c.title || `Criterion ${i + 1}`,
    state: c.state || c.status || 'pending',
    owner: c.ownerUserId || c.owner || null,
    evidence: c.evidence || c.note || null,
    description: c.description,
    evidenceArtifactIds: Array.isArray(c.evidenceArtifactIds) ? c.evidenceArtifactIds : [],
  }));
  const criterionPager = useClientPagination(criteria, {
    initialPageSize: 10,
    resetKey: gateId,
  });
  const docs = artifacts.data?.data || [];
  const docsPager = useClientPagination(docs, {
    initialPageSize: 5,
    resetKey: projectId,
  });

  usePageMetadata({
    title: p?.name ? `${p.name} · ${g?.name || `Gate ${g?.stage || ''}`}` : 'Gate Review',
    subtitle: g ? `${gateId} · ${p?.lifecycleStage || g?.stage || ''}` : undefined,
  });

  const gateList = (gates.data?.data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const finRecords = useMemo(() => financials.data?.data || [], [financials.data]);
  const conflictEdges = (lineage.data?.data || []).filter((l) => l.conflictFlag || l.conflict);

  const stages = useMemo(() => {
    const activeIdx = gateList.findIndex((gg) => gg.id === gateId);
    return gateList.map((gg, idx) => ({
      id: gg.id,
      label: gg.name || gg.title || `Gate ${idx + 1}`,
      status: gateStatus(gg, idx, activeIdx === -1 ? idx : activeIdx),
      richTooltip: gg.richTooltip || null,
    }));
  }, [gateList, gateId]);

  // Prefer the project-level approved-plan vs forecast-at-completion summary.
  // Funding-source and budget-line rows are drilldown material and can distort the compact callout.
  const conflictView = useMemo(() => {
    const visual = p?.visuals?.financialConflict;
    const visualTrust = visual?.richTooltip?.trust || {};
    const visualView = makeFinancialConflictView({
      source: visual?.sourceValueUsdM,
      target: visual?.targetValueUsdM,
      sourceLabel: visual?.sourceLabel || 'Approved plan',
      targetLabel: visual?.targetLabel || 'Forecast at completion',
      freshness: visual?.freshness || visualTrust.freshness,
      confidence: visual?.confidence ?? visualTrust.confidence,
    });
    if (visualView) return visualView;

    const summary = p?.budgetSummary || p?.financialSummary || {};
    const summaryView = makeFinancialConflictView({
      source: summary.plannedBudgetUsdM,
      target: summary.forecastAtCompletionUsdM,
      freshness: p?.dataTrust?.freshness,
      confidence: p?.dataTrust?.confidence,
    });
    if (summaryView) return summaryView;

    const summaryRow = finRecords.find(
      (r) =>
        finiteFinancialNumber(r.plannedBudgetUsdM) != null &&
        finiteFinancialNumber(r.forecastAtCompletionUsdM) != null,
    );
    if (summaryRow) {
      const trust = trustFromRecord(summaryRow);
      return makeFinancialConflictView({
        source: summaryRow.plannedBudgetUsdM,
        target: summaryRow.forecastAtCompletionUsdM,
        freshness: trust.freshness,
        confidence: trust.confidence,
      });
    }

    const planForecastRows = finRecords.filter(
      (r) =>
        finiteFinancialNumber(r.plannedUsdM ?? r.plannedBudgetUsdM) != null &&
        finiteFinancialNumber(r.forecastAtCompletionUsdM) != null,
    );
    if (!planForecastRows.length) return null;
    const aggregate = planForecastRows.reduce(
      (acc, row) => ({
        source: acc.source + finiteFinancialNumber(row.plannedUsdM ?? row.plannedBudgetUsdM),
        target: acc.target + finiteFinancialNumber(row.forecastAtCompletionUsdM),
      }),
      { source: 0, target: 0 },
    );
    const trust = trustFromRecord(planForecastRows[0]);
    return makeFinancialConflictView({
      source: aggregate.source,
      target: aggregate.target,
      freshness: trust.freshness,
      confidence: trust.confidence,
    });
  }, [finRecords, p]);
  const financialConflictTooltip = p?.visuals?.financialConflict?.richTooltip || null;

  // AC-TRUST-4 / AC-P3M-6: never assert a clean state when the underlying
  // check did not actually run. Each verification zone resolves to one of:
  //   'verified'     — data present; findings (or their absence) are real
  //   'not-verified' — data absent or fetch failed; render "Not verified — {reason}"
  //   'access-gated' — the current role cannot run the check
  //   'loading'      — check still in flight
  const isAccessError = (err) => err && (err.status === 401 || err.status === 403);
  const financialVerification = (() => {
    if (conflictView) return { state: 'verified' };
    if (financials.loading) return { state: 'loading' };
    if (financials.error) {
      if (isAccessError(financials.error)) {
        return {
          state: 'not-verified',
          reason: 'requires financial data access',
          detail:
            'The variance check could not run because your role cannot read this project’s financial records.',
        };
      }
      return {
        state: 'not-verified',
        reason: 'financial records failed to load',
        detail: `The variance check did not run: ${financials.error.message}`,
      };
    }
    return {
      state: 'not-verified',
      reason: 'financial data unavailable',
      detail:
        'No approved-plan vs forecast-at-completion records exist for this project, so the variance check could not run.',
    };
  })();
  const lineageVerification = (() => {
    if (!canSeeLineage) {
      return {
        state: 'access-gated',
        reason: 'requires Data Steward access',
        detail:
          'The source-to-canonical conflict check runs against the lineage explorer, which your role cannot read.',
      };
    }
    if (lineage.loading) return { state: 'loading' };
    if (lineage.error) {
      if (isAccessError(lineage.error)) {
        return {
          state: 'not-verified',
          reason: 'requires Data Steward access',
          detail: 'The lineage conflict check was denied for your role.',
        };
      }
      return {
        state: 'not-verified',
        reason: 'lineage records failed to load',
        detail: `The conflict check did not run: ${lineage.error.message}`,
      };
    }
    if (!lineage.data) {
      return {
        state: 'not-verified',
        reason: 'lineage data unavailable',
        detail: 'No lineage records were returned, so the conflict check could not run.',
      };
    }
    return { state: 'verified' };
  })();

  // Compact lifecycle-progression ribbon. Active stage = this gate's stage;
  // a denied/rejected gate forces a 'blocked' tone on its stage (denied/returned
  // -> 'blocked', per WorkflowPipeline contract).
  const lifecycleSteps = useMemo(() => {
    const stage = String(g?.stage || '').trim();
    const current = LIFECYCLE_ORDER.includes(stage) ? stage : null;
    const blockedStatus = String(g?.status || '')
      .toLowerCase()
      .replace(/[^a-z]/g, '');
    const statusByKey =
      current && (blockedStatus === 'rejected' || blockedStatus === 'revisionrequested')
        ? { [current]: 'blocked' }
        : {};
    return buildPipelineSteps({
      order: LIFECYCLE_ORDER,
      current,
      statusByKey,
      labels: LIFECYCLE_LABELS,
    });
  }, [g?.stage, g?.status]);

  // Budget-execution waterfall (planned → obligated → actual → forecast). Built
  // ONLY from real seeded financial fields; each step is omitted unless the
  // underlying number is present, and the whole card is omitted if we cannot
  // form a meaningful planned + forecast pair.
  const waterfallItems = useMemo(() => {
    const summary =
      p?.budgetSummary ||
      p?.financialSummary ||
      finRecords.find((r) => finiteFinancialNumber(r.plannedBudgetUsdM) != null) ||
      {};
    const planned = finiteFinancialNumber(summary.plannedBudgetUsdM ?? summary.plannedUsdM);
    const obligated = finiteFinancialNumber(summary.obligatedUsdM);
    const actual = finiteFinancialNumber(summary.actualSpendUsdM ?? summary.actualUsdM);
    const forecast = finiteFinancialNumber(summary.forecastAtCompletionUsdM);
    if (planned == null || forecast == null) return [];
    const items = [{ label: 'Planned', value: planned, type: 'baseline' }];
    let prev = planned;
    if (obligated != null) {
      items.push({
        label: 'Obligated',
        value: Number((obligated - prev).toFixed(2)),
        type: 'delta',
      });
      prev = obligated;
    }
    if (actual != null) {
      items.push({ label: 'Actual', value: Number((actual - prev).toFixed(2)), type: 'delta' });
    }
    items.push({ label: 'Forecast', value: forecast, type: 'forecast' });
    return items;
  }, [finRecords, p]);

  // Executive KPI band — every value sourced from already-fetched data; no
  // fabrication. Tiles are omitted when their underlying field is absent.
  const kpiItems = useMemo(() => {
    const items = [];
    const total = criteria.length;
    const complete = criteria.filter(
      (c) => c.state === 'complete' || c.state === 'verified',
    ).length;
    const blocked = criteria.filter((c) => c.state === 'blocked').length;
    const pct = total ? Math.round((complete / total) * 100) : 0;
    if (total) {
      items.push({
        id: 'criteria',
        label: 'Criteria verified',
        value: `${complete}/${total}`,
        icon: '✓',
        deltaLabel: `${pct}% complete`,
        deltaTone: pct >= 80 ? 'sage' : pct >= 50 ? 'amber' : 'brick',
        status: blocked ? 'brick' : pct >= 80 ? 'sage' : 'amber',
        statusLabel: blocked ? `${blocked} blocked` : pct >= 80 ? 'On track' : 'In progress',
        footer: 'Gate checklist completion',
      });
    }
    if (conflictView) {
      const variance = Number(conflictView.variance);
      const variancePct = conflictView.source
        ? Math.round((variance / Number(conflictView.source)) * 100)
        : null;
      const overrun = variance > 0;
      items.push({
        id: 'variance',
        label: 'Forecast variance',
        value: `${variance > 0 ? '+' : ''}${variance.toFixed(1)}`,
        unit: '$M',
        icon: '$',
        deltaLabel:
          variancePct != null
            ? `${variancePct > 0 ? '+' : ''}${variancePct}% vs plan`
            : 'vs approved plan',
        deltaTone: overrun ? 'brick' : 'sage',
        status: overrun ? 'amber' : 'sage',
        statusLabel: overrun ? 'Over plan' : 'Within plan',
        footer: `${conflictView.sourceLabel} → ${conflictView.targetLabel}`,
      });
    }
    const overall = finiteFinancialNumber(p?.healthScores?.overall);
    if (overall != null) {
      const tone = deriveKpiTone({ value: overall, target: 75 });
      items.push({
        id: 'health',
        label: 'Project health',
        value: overall,
        unit: '/100',
        icon: '◈',
        status: tone,
        statusLabel: overall >= 75 ? 'Healthy' : overall >= 50 ? 'Watch' : 'At risk',
        footer: 'Composite delivery + cost + risk',
      });
    }
    const decisionDate = g?.decisionForumDate || p?.gateSnapshot?.nextDecisionForum;
    const days = daysUntil(decisionDate);
    if (days != null) {
      items.push({
        id: 'decision',
        label: 'Decision forum',
        value: days >= 0 ? days : Math.abs(days),
        unit: days === 1 || days === -1 ? 'day' : 'days',
        icon: '⌛',
        deltaLabel: days >= 0 ? 'until forum' : 'overdue',
        deltaTone: days < 0 ? 'brick' : days <= 7 ? 'amber' : 'slate',
        status: days < 0 ? 'brick' : days <= 7 ? 'amber' : 'blue',
        statusLabel: new Date(decisionDate).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
        footer: 'Next gate decision board',
      });
    }
    return items;
  }, [criteria, conflictView, p, g]);

  const lineageConflictRoute = conflictEdges.length
    ? `/admin/lineage?targetType=${conflictEdges[0].canonicalEntityType || conflictEdges[0].targetEntityType || 'project'}&targetId=${conflictEdges[0].canonicalEntityId || conflictEdges[0].targetEntityId || projectId}`
    : null;
  const lineageConflictTooltip =
    conflictEdges[0]?.edgeTooltip && lineageConflictRoute
      ? {
          ...conflictEdges[0].edgeTooltip,
          drillTarget: {
            kind: 'route',
            label: 'Open lineage drilldown',
            href: lineageConflictRoute,
          },
        }
      : null;

  async function draftBrief() {
    try {
      const res = await aiDraft.run(() =>
        api.post('/ai/generate/gate-review-brief', {
          prompt: `Draft a Gate ${g?.stage || gateId} review brief for ${p?.name || projectId}.`,
          context: { projectId, gateId },
        }),
      );
      setAiBrief(res.data || res);
      toast.success({
        title: 'AI gate brief drafted',
        body: 'Pending human review. Citations link to source artifacts.',
      });
      activityRail.push({
        action: 'ai.generate.gate-review-brief',
        targetType: 'ai_run',
        targetId: res.data?.id || 'AIRUN-NEW',
        actorUserId: user?.id || 'unknown',
        summary: `Gate brief for ${gateId} (pending review)`,
      });
    } catch (e) {
      toast.error({ title: 'Could not draft brief', body: e.message });
    }
  }

  async function decide(action, rationale) {
    const typed = String(rationale || '').trim();
    const briefText = useAiRationale && aiBrief?.summary ? String(aiBrief.summary).trim() : '';
    const fullRationale = [typed, briefText ? `AI brief (opt-in): ${briefText}` : '']
      .filter(Boolean)
      .join('\n\n');
    if (!fullRationale) {
      toast.error({
        title: 'Rationale required',
        body: 'Enter a decision rationale (or opt in to using the AI brief) before recording the decision.',
      });
      return;
    }
    try {
      const res = await decision.run(() =>
        api.post(`/projects/${projectId}/gates/${gateId}/decision`, {
          action,
          rationale: fullRationale,
        }),
      );
      const next = res.data?.status || action;
      const updated = res.data || res;
      gate.setData?.((prev) => ({ ...prev, data: updated }));
      toast.success({
        title: `Gate ${action}`,
        body: `Status → ${next}. Audit event recorded.`,
        action: { label: 'Open exports', onClick: () => navigate('/exports') },
      });
      activityRail.push({
        action: `gate.${action}`,
        targetType: 'lifecycle_gate',
        targetId: gateId,
        actorUserId: user?.id || 'unknown',
        summary: `${action} · ${p?.name || projectId}`,
      });
    } catch (e) {
      toast.error({ title: `Could not ${action}`, body: e.message });
    }
  }

  async function handleAttachEvidence(payload) {
    try {
      const createRes = await attach.run(() =>
        api.post('/artifacts', {
          title: payload.title,
          documentType: payload.documentType,
          classificationLabel: payload.classificationLabel,
          projectId,
          portfolioId: p?.portfolioId,
          programId: p?.programId,
          relatedGateIds: [gateId],
          relatedCriterionId: payload.criterionId,
          summary: payload.summary,
        }),
      );
      const artifact = createRes.data || createRes;
      await api.post(`/gates/${gateId}/evidence`, {
        criterionId: payload.criterionId,
        artifactId: artifact.id,
      });
      // Optimistic local UI updates.
      artifacts.setData?.((prev) =>
        prev
          ? {
              ...prev,
              data: { ...(prev.data || {}), data: [artifact, ...(prev.data?.data || [])] },
            }
          : prev,
      );
      gate.setData?.((prev) => {
        if (!prev?.data) return prev;
        const g = prev.data;
        const nextCriteria = (g.criteria || []).map((c) =>
          c.id === payload.criterionId
            ? {
                ...c,
                evidenceArtifactIds: [...(c.evidenceArtifactIds || []), artifact.id],
                state: c.state === 'pending' || c.state === 'blocked' ? 'complete' : c.state,
              }
            : c,
        );
        return { ...prev, data: { ...g, criteria: nextCriteria } };
      });
      setAttachOpen(false);
      toast.success({
        title: 'Evidence attached',
        body: `${artifact.title} linked to ${payload.criterionId}.`,
      });
      activityRail.push({
        action: 'artifact.create',
        targetType: 'artifact',
        targetId: artifact.id,
        actorUserId: user?.id || 'unknown',
        summary: `Attached ${artifact.title} to ${payload.criterionId} on ${gateId}`,
      });
    } catch (e) {
      toast.error({ title: 'Could not attach evidence', body: e.message });
    }
  }

  if (project.loading || gate.loading) return <LoadingSkeleton rows={6} />;
  if (project.error) return <ErrorState error={project.error} />;
  if (gate.error) return <ErrorState error={gate.error} />;
  if (!p || !g)
    return (
      <EmptyState
        title="Gate not found"
        description="Verify the gate ID exists in this portfolio."
      />
    );

  return (
    <>
      <Breadcrumbs
        items={[
          { to: '/projects', label: 'Projects' },
          { to: `/projects/${projectId}`, label: p.name },
          { label: g.name || `Gate ${g.stage || gateId}` },
        ]}
      />
      {/* PRIME-SHELL-UX-001 revise 2: single body h1 for the gate-review detail
          route, read from the record metadata registered via usePageMetadata. */}
      <PageHeader />
      <PageActionStrip
        filters={
          <>
            <HealthIndicator
              health={g.status === 'approved' ? 'green' : g.status === 'rejected' ? 'red' : 'amber'}
            />
            <StatusChip status={g.status || 'in_review'} />
            <span className="chip outline sm">
              {criteria.filter((c) => c.state === 'complete').length} / {criteria.length} criteria
              complete
            </span>
          </>
        }
        actions={
          <button className="btn ghost" onClick={draftBrief} disabled={aiDraft.running}>
            {aiDraft.running ? 'Drafting AI brief…' : 'Draft AI brief'}
          </button>
        }
      />
      {kpiItems.length ? (
        <KpiHeroBand
          className="gate-kpi-band"
          eyebrow="Gate readiness at a glance"
          asOf={gate.loadedAt || project.loadedAt || undefined}
          items={kpiItems}
          columns={Math.min(4, kpiItems.length)}
        />
      ) : null}

      <div className="gate-hero">
        <div className="gate-pipeline-wrap">
          {stages.length ? (
            <StagePipeline
              stages={stages}
              variant="chevron"
              selectedId={gateId}
              onSelect={(s) => navigate(`/projects/${projectId}/gate-review/${s.id}`)}
            />
          ) : null}
        </div>
        {lifecycleSteps.length ? (
          <div className="gate-hero__lifecycle">
            <div className="gate-hero__lifecycle-head">
              <span className="eyebrow">Lifecycle progression</span>
              <span className="muted small">
                {LIFECYCLE_LABELS[g?.stage] || g?.stage || 'Stage'} stage · governed gate path
              </span>
            </div>
            <WorkflowPipeline steps={lifecycleSteps} ariaLabel="Project lifecycle progression" />
          </div>
        ) : null}
      </div>

      <section className="bento gate-bento">
        <div className="span-7 card flush gate-criteria">
          <header
            className="row spread"
            style={{ padding: 'var(--space-5)', borderBottom: 'var(--hairline)' }}
          >
            <div>
              <div className="eyebrow">Gate review criteria</div>
              <h2 className="card-title">{g.name || `Gate ${g.stage || gateId}`}</h2>
              <p className="muted">
                {criteria.length === 0
                  ? 'No criteria captured on this gate.'
                  : `${criteria.length} criteria · ${criteria.filter((c) => c.state === 'complete').length} verified`}
              </p>
              {criteria.length ? (
                <p className="muted small">
                  Readiness % is a derived indicator — derived from gate checklist state, not a
                  measured value.
                </p>
              ) : null}
            </div>
          </header>
          {criteria.length === 0 ? (
            <EmptyState
              title="No criteria recorded"
              description="No gate checklist criteria have been recorded yet."
            />
          ) : (
            <ul className="gate-criterion-list">
              {criterionPager.items.map((c) => (
                <li key={c.id} className={`gate-criterion gate-${c.state || 'pending'}`}>
                  <div className="gate-criterion-state" aria-hidden>
                    {c.state === 'complete' ? '✓' : c.state === 'blocked' ? '!' : '○'}
                  </div>
                  <div className="gate-criterion-body">
                    <div className="row spread">
                      <span className="gate-criterion-title">
                        <strong>{c.title}</strong>
                        <span className={`chip ${toneForCriterion(c.state)} sm`}>
                          {(() => {
                            const m = {
                              complete: 'Complete',
                              verified: 'Verified',
                              pending: 'Pending',
                              inReview: 'In review',
                              in_review: 'In review',
                              blocked: 'Blocked',
                              atRisk: 'At risk',
                              at_risk: 'At risk',
                              exception: 'Exception',
                            };
                            const s = c.state || 'pending';
                            return (
                              m[s] ||
                              String(s)
                                .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                                .replace(/[_-]+/g, ' ')
                                .replace(/^\w/, (ch) => ch.toUpperCase())
                            );
                          })()}
                        </span>
                      </span>
                      <span className="gate-criterion-score">
                        <InlineScoreCell
                          value={criterionScore(c.state)}
                          max={100}
                          tone={criterionScoreTone(c.state)}
                          suffix="%"
                          ariaLabel={`${c.title} derived readiness ${criterionScore(c.state)} percent (derived from gate checklist state)`}
                        />
                      </span>
                    </div>
                    {c.description ? <p className="muted small">{c.description}</p> : null}
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className="muted small">Owner · {c.owner || 'unassigned'}</span>
                      {c.evidenceArtifactIds.length ? (
                        <span className="chip sage sm" data-testid="criterion-evidence-count">
                          ✓ {c.evidenceArtifactIds.length} evidence linked
                        </span>
                      ) : null}
                      {c.evidence ? (
                        <span className="chip outline sm">
                          evidence: {String(c.evidence).slice(0, 32)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <PageViewerControls
            page={criterionPager.page}
            label="criteria"
            pageSize={criterionPager.pageSize}
            onOffsetChange={criterionPager.setOffset}
            onPageSizeChange={criterionPager.setPageSize}
          />
        </div>

        <div className="span-5 stack">
          <div className="gate-financial-stack">
            {conflictView ? (
              <FinancialConflictCard
                id={projectId}
                sourceLabel={conflictView.sourceLabel}
                targetLabel={conflictView.targetLabel}
                sourceValueUsdM={Number(conflictView.source)}
                targetValueUsdM={Number(conflictView.target)}
                varianceUsdM={Number(conflictView.variance)}
                freshness={conflictView.freshness}
                confidence={conflictView.confidence}
                richTooltip={financialConflictTooltip}
                onDrill={() => navigate(`/financials?focus=${projectId}`)}
              />
            ) : financialVerification.state === 'loading' ? (
              <LoadingSkeleton rows={2} />
            ) : (
              <AlertCard
                severity="amber"
                label="NOT VERIFIED"
                title={`Not verified — ${financialVerification.reason}`}
                body={financialVerification.detail}
                testId="financial-not-verified"
              />
            )}
            {waterfallItems.length ? (
              <div className="card gate-waterfall-card">
                <header className="row spread">
                  <div>
                    <div className="eyebrow">Budget execution</div>
                    <h2 className="card-title">Planned → forecast at completion</h2>
                  </div>
                  <button
                    className="btn ghost sm"
                    onClick={() => navigate(`/financials?focus=${projectId}`)}
                  >
                    Open financials
                  </button>
                </header>
                <FinancialWaterfall
                  items={waterfallItems}
                  height={220}
                  ariaLabel={`Budget execution waterfall for ${p.name}`}
                />
                <div className="gate-waterfall-legend">
                  <span className="gate-waterfall-legend__item">
                    <span className="gate-waterfall-legend__swatch is-baseline" aria-hidden />
                    Planned baseline
                  </span>
                  <span className="gate-waterfall-legend__item">
                    <span className="gate-waterfall-legend__swatch is-delta-up" aria-hidden />
                    Spend draw-down
                  </span>
                  <span className="gate-waterfall-legend__item">
                    <span className="gate-waterfall-legend__swatch is-forecast" aria-hidden />
                    Forecast at completion
                  </span>
                </div>
              </div>
            ) : null}
          </div>
          {conflictEdges.length ? (
            <LineageConflictCard
              id={conflictEdges[0].id || gateId}
              sourceSystem={conflictEdges[0].sourceSystem || 'PMRT'}
              sourceRecord={conflictEdges[0].sourceRecordId || conflictEdges[0].sourceEntityId}
              canonicalEntity={
                conflictEdges[0].canonicalEntityType || conflictEdges[0].targetEntityType
              }
              canonicalEntityId={
                conflictEdges[0].canonicalEntityId || conflictEdges[0].targetEntityId
              }
              field={conflictEdges[0].canonicalField || conflictEdges[0].sourceField}
              conflictNote={
                conflictEdges[0].transformation ||
                'Source vs canonical mapping is disputed — requires data steward reconciliation.'
              }
              richTooltip={lineageConflictTooltip}
              onOpen={() => navigate(lineageConflictRoute)}
            />
          ) : lineageVerification.state === 'verified' ? (
            <AlertCard
              severity="blue"
              title="No lineage conflicts"
              body="The conflict-only lineage check ran for this project and returned no source-to-canonical disputes."
              testId="lineage-verified-clean"
            />
          ) : lineageVerification.state === 'loading' ? (
            <LoadingSkeleton rows={2} />
          ) : (
            <AlertCard
              severity="amber"
              label="NOT VERIFIED"
              title={`Not verified — ${lineageVerification.reason}`}
              body={lineageVerification.detail}
              testId="lineage-not-verified"
            />
          )}
        </div>

        <div className="span-7 card gate-evidence">
          <header className="row spread">
            <div>
              <div className="eyebrow">Evidence & artifacts</div>
              <h2 className="card-title">Linked documents</h2>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <span className="chip outline sm">{docs.length} attached</span>
              <button
                className="btn primary sm tactile-press"
                onClick={() => setAttachOpen(true)}
                disabled={!criteria.length}
              >
                Attach Evidence
              </button>
            </div>
          </header>
          <div className="row gate-provenance-row" data-testid="gate-artifact-provenance">
            {docsPager.items.map((artifact) => (
              <span key={`provenance-${artifact.id}`} className="row gate-provenance-item">
                <span className="muted small gate-provenance-title" title={artifact.title || artifact.id}>
                  {artifact.title || artifact.id}
                </span>
                <ProvenanceBadge value={artifact} kind="artifact" />
              </span>
            ))}
          </div>
          <EvidenceList
            artifacts={docsPager.items.map((d) => ({
              ...d,
              type: d.documentType || d.type,
              aiGenerated: /ai|brief|generated/i.test(d.title || d.documentType || ''),
            }))}
            onOpenArtifact={(art) => navigate(`/ai/orchestration?artifactId=${art.id}`)}
          />
          <PageViewerControls
            page={docsPager.page}
            label="documents"
            pageSize={docsPager.pageSize}
            onOffsetChange={docsPager.setOffset}
            onPageSizeChange={docsPager.setPageSize}
          />
        </div>

        <div className="span-5">
          {aiBrief ? (
            <label
              className="row"
              style={{ gap: 8, alignItems: 'center', padding: 'var(--space-2) 0' }}
            >
              <input
                type="checkbox"
                checked={useAiRationale}
                onChange={(e) => setUseAiRationale(e.target.checked)}
              />
              <span>
                Use AI brief as rationale
                <span className="muted small" style={{ display: 'block' }}>
                  Opt-in only — the drafted brief is appended to your typed rationale and named as
                  AI-generated in the audit record.
                </span>
              </span>
            </label>
          ) : null}
          <GateDecisionPanel
            disabled={decision.running}
            recommendation={
              aiBrief?.summary
                ? 'AI brief drafted and pending human review — opt in above to attach it as rationale.'
                : 'Draft an AI brief or proceed directly to record your decision.'
            }
            onApprove={(rationale) => decide('approve', rationale)}
            onRequestRevision={(rationale) => decide('requestRevision', rationale)}
            onDefer={(rationale) => decide('defer', rationale)}
            onReject={(rationale) => decide('reject', rationale)}
          />
        </div>

        <div className="span-12 card gate-ai">
          <header className="row spread">
            <div>
              <div className="eyebrow">AI gate-review brief</div>
              <h2 className="card-title">Generated by AGT-GATE-REVIEW · pending human review</h2>
            </div>
            <button className="btn outline sm" onClick={draftBrief} disabled={aiDraft.running}>
              {aiBrief ? 'Regenerate' : 'Draft AI brief'}
            </button>
          </header>
          {aiBrief ? (
            <div className="gate-ai-body">
              <p className="gate-ai-summary">{aiBrief.summary || '(no summary)'}</p>
              <pre className="gate-ai-content">{aiBrief.content}</pre>
              <AiBriefCitations citations={aiBrief.citations || []} />
              <p className="muted small">Awaiting human approval.</p>
            </div>
          ) : (
            <EmptyState
              title="No AI brief drafted"
              description="Click 'Draft AI brief' to generate a grounded summary with citation chips."
            />
          )}
        </div>
      </section>

      <AttachEvidenceDrawer
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        criteria={criteria}
        onSubmit={handleAttachEvidence}
        submitting={attach.running}
      />
    </>
  );
}

function AiBriefCitations({ citations }) {
  const pager = useClientPagination(citations, {
    initialPageSize: 5,
    resetKey: `citations-${citations.length}`,
  });
  if (!citations.length) return null;
  return (
    <div>
      <div className="eyebrow">Citations</div>
      <AiCitationStrip
        citations={pager.items}
        onOpen={(c, resolved) => {
          toast.show({
            title: c.source,
            body: resolved?.title || c.excerpt,
          });
        }}
      />
      <PageViewerControls
        page={pager.page}
        label="citations"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </div>
  );
}

function toneForCriterion(state) {
  switch (state) {
    case 'complete':
      return 'sage';
    case 'pending':
      return 'blue';
    case 'blocked':
      return 'red';
    case 'exception':
      return 'amber';
    case 'missing':
      return 'outline';
    default:
      return 'outline';
  }
}
